import { UIEvent, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { MultiDropdown } from 'shared/ui/multi-dropdown';
import { RELEVANT_DOMAINS, RelevantDomains } from 'shared/lib/domains';
import {
  eventService,
  generateProductFilterResultEventPayload,
  generateCommonContextEventPayload,
  getPage,
  XApiEventKeys,
} from 'shared/api/event-service';
// eslint-disable-next-line boundaries/element-types
import { useIsEntitled } from 'features/authorization';
import { TX_ASSET_BK } from 'shared/config/entitelment-constants';

import {
  setSelectedSubjects,
  useSelectedClassKey,
  useSelectedSubjects,
} from '../model';
import { ALL_SUBJECTS } from '../constants';

import styles from './styles.module.scss';

const cklaKey = 'ckla';
const cklaPilotKey = 'cklaPilot';
const elaKey = 'ela';
const scienceKey = 'science';
const mathKey = 'math';
const caminosKey = 'caminos';

const SUBJECTS_MAP: Record<string, RelevantDomains> = {
  [cklaKey]: 'CKLA',
  [cklaPilotKey]: 'CKLA Pilot',
  [elaKey]: 'ELA',
  [scienceKey]: 'Science',
  [mathKey]: 'Math',
  [caminosKey]: 'Caminos',
};
const dropdownList = [
  { id: ALL_SUBJECTS, text: 'All' },
  { id: cklaKey, text: 'CKLA' },
  { id: cklaPilotKey, text: 'CKLA Pilot' },
  { id: elaKey, text: 'ELA' },
  { id: scienceKey, text: 'Science' },
  { id: mathKey, text: 'Math' },
  { id: caminosKey, text: 'Caminos' },
];

export const ProgramsFilter = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedSubjects = useSelectedSubjects();
  const selectedClassKey = useSelectedClassKey();
  const { isAssetFound: hasTxAsset } = useIsEntitled(TX_ASSET_BK);

  const [selectedIds, setSelectedIds] = useState([ALL_SUBJECTS]);
  const [dropdownText, setDropdownText] = useState(
    `${t('Programs')}: ${t('All')}`,
  );

  const isFirstRender = useRef(true);
  // have to monitor prev selected ids to correctly manage its switching
  const prevSelectedIds = useRef<string[]>([]);
  const prevSelectedClassKey = useRef(selectedClassKey);

  if (hasTxAsset) {
    const index = dropdownList.findIndex((item) => item.id === elaKey);

    if (index !== -1) {
      dropdownList[index] = { id: elaKey, text: 'ELAR' };
    }
  }

  const updatePrevSelectedIds = (ids: string[]) =>
    (prevSelectedIds.current = ids);

  const handleIdsSwitch = (ids: string[]) => {
    let updatedIds = [...ids];

    // last selected key is "All"
    if (updatedIds[updatedIds.length - 1] === ALL_SUBJECTS) {
      updatedIds = [ALL_SUBJECTS];

      setSelectedIds(updatedIds);
      updatePrevSelectedIds(updatedIds);
      dispatch(setSelectedSubjects([]));

      return;
    }

    const allSubjectsKeyIndex = updatedIds.indexOf(ALL_SUBJECTS);

    // if "All" key was selected before
    if (allSubjectsKeyIndex >= 0) {
      updatedIds.splice(allSubjectsKeyIndex, 1);
    }

    const updatedSelectedSubjects = updatedIds.reduce(
      (acc: RelevantDomains[], id) => {
        return [...acc, SUBJECTS_MAP[id]];
      },
      [],
    );

    setSelectedIds(updatedIds);
    updatePrevSelectedIds(updatedIds);
    dispatch(setSelectedSubjects(updatedSelectedSubjects));
  };

  const handleEmptyIdsSwitch = () => {
    let updatedIds: string[];
    let updatedSelectedSubjects: RelevantDomains[];

    if (prevSelectedIds.current.includes(ALL_SUBJECTS)) {
      updatedIds = Object.keys(SUBJECTS_MAP);
      updatedSelectedSubjects = [...RELEVANT_DOMAINS];
    } else {
      updatedIds = [ALL_SUBJECTS];
      updatedSelectedSubjects = [];
    }

    setSelectedIds(updatedIds);
    updatePrevSelectedIds(updatedIds);
    dispatch(setSelectedSubjects(updatedSelectedSubjects));
  };

  const handleSelect = (ids: string[]) =>
    ids.length ? handleIdsSwitch(ids) : handleEmptyIdsSwitch();

  const onDropdownClose = (e?: UIEvent<HTMLElement>) => {
    const productFilterIsAll = selectedIds[0] === ALL_SUBJECTS;
    const productFiltersApplied = productFilterIsAll
      ? Object.values(SUBJECTS_MAP)
      : selectedIds;

    eventService.send(
      XApiEventKeys.productFiltered,
      generateProductFilterResultEventPayload({
        productFiltersApplied,
        productFilterIsAll,
      }),
      generateCommonContextEventPayload({
        url: window.location.href,
        page: getPage(),
        isRightClick: e?.type === 'contextmenu',
      }),
    );
  };

  // initial set up of selected ids according to the store state
  useEffect(() => {
    if (!isFirstRender.current) return;

    isFirstRender.current = false;

    let updatedIds;
    const areAllSubjectsSelected =
      selectedSubjects.length === RELEVANT_DOMAINS.length;

    if (areAllSubjectsSelected || !selectedSubjects.length) {
      updatedIds = [ALL_SUBJECTS];

      setSelectedIds(updatedIds);
      updatePrevSelectedIds(updatedIds);
      return;
    }

    updatedIds = Object.entries(SUBJECTS_MAP).reduce(
      (acc: string[], [subjectKey, subjectValue]) => {
        if (selectedSubjects.includes(subjectValue)) {
          return [...acc, subjectKey];
        }

        return acc;
      },
      [],
    );

    setSelectedIds(updatedIds);
    updatePrevSelectedIds(updatedIds);
  }, [selectedSubjects]);

  // reset subjects on class change or if all subjects are deselected
  useEffect(() => {
    const hasClassChanged =
      !!prevSelectedClassKey.current.length &&
      selectedClassKey.join('') !== prevSelectedClassKey.current.join('');

    if (!selectedSubjects.length || hasClassChanged) {
      setSelectedIds([ALL_SUBJECTS]);
      updatePrevSelectedIds([ALL_SUBJECTS]);
    }

    prevSelectedClassKey.current = selectedClassKey;
  }, [selectedClassKey, selectedSubjects]);

  useEffect(() => {
    const selectedIdsStr = selectedIds
      .map((id) => {
        const listItem = dropdownList.find((item) => item.id === id);

        if (!listItem) {
          return 'All';
        }

        return listItem.text;
      })
      .join(', ');

    setDropdownText(`${t('Program')}: ${selectedIdsStr}`);
  }, [selectedIds]);

  return (
    <MultiDropdown
      list={dropdownList}
      classNames={{
        button: clsx('rhythm-button', styles.dropdownBtn),
        container: styles.dropdownContainer,
        menu: styles.dropdownMenu,
      }}
      dropdownText={dropdownText}
      onSelect={handleSelect}
      selectedIds={selectedIds}
      onClose={onDropdownClose}
      testId="programs-filter"
    />
  );
};
